import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import { supabase } from '../../lib/supabase'
import { Course, ExamRun, Price, Entitlement } from '../../lib/types'
import { MainLayout } from '../../components/layouts/MainLayout'
import { Loading } from '../../components/common/Loading'
import { ErrorMessage } from '../../components/common/ErrorMessage'
import { BookOpen, Calendar, DollarSign, Clock, CheckCircle, AlertCircle } from 'lucide-react'

export function EnrollmentConfirmPage() {
  useDocumentTitle('申込確認')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [course, setCourse] = useState<Course | null>(null)
  const [_examRun, setExamRun] = useState<ExamRun | null>(null)
  const [price, setPrice] = useState<Price | null>(null)
  const [existingEntitlement, setExistingEntitlement] = useState<Entitlement | null>(null)
  const [creating, setCreating] = useState(false)

  const coursePublicKey = searchParams.get('course')
  const runPublicKey = searchParams.get('run')

  useEffect(() => {
    if (!user) {
      navigate('/login')
      return
    }

    if (!coursePublicKey) {
      setError('講座情報が指定されていません')
      setLoading(false)
      return
    }

    fetchEnrollmentData()
  }, [coursePublicKey, runPublicKey, user])

  const fetchEnrollmentData = async () => {
    if (!coursePublicKey || !user) return

    try {
      setLoading(true)
      setError(null)

      // 講座情報を取得
      const { data: courseData, error: courseError } = await supabase
        .from('courses')
        .select('*')
        .eq('course_public_key', coursePublicKey)
        .eq('status', 'published')
        .single()

      if (courseError || !courseData) {
        setError('指定された講座が見つかりませんでした')
        setLoading(false)
        return
      }

      setCourse(courseData)

      // exam_run情報を取得（指定されている場合）
      if (runPublicKey) {
        const { data: runData, error: runError } = await supabase
          .from('exam_runs')
          .select('*')
          .eq('run_public_key', runPublicKey)
          .single()

        if (!runError && runData) {
          setExamRun(runData)
        }
      }

      // 既存のentitlementをチェック（重複防止）
      const { data: entitlementData } = await supabase
        .from('entitlements')
        .select('*')
        .eq('user_id', user.id)
        .eq('course_id', courseData.course_id)
        .eq('status', 'active')
        .single()

      if (entitlementData) {
        setExistingEntitlement(entitlementData)
      }

      // 価格情報を取得（price_type='standard'）
      const { data: priceData, error: priceError } = await supabase
        .from('prices')
        .select('*')
        .eq('course_id', courseData.course_id)
        .eq('price_type', 'standard')
        .single()

      if (priceError || !priceData) {
        setError('価格情報が見つかりませんでした')
        setLoading(false)
        return
      }

      setPrice(priceData)
    } catch (err: any) {
      console.error('Error fetching enrollment data:', err)
      setError(err.message || 'データの取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleCheckout = async () => {
    if (!course || !price || !user) return

    try {
      setCreating(true)
      setError(null)

      const bffUrl = import.meta.env.VITE_BFF_URL
      const token = (await supabase.auth.getSession()).data.session?.access_token

      if (!token) {
        throw new Error('認証トークンが取得できませんでした')
      }

      const body: any = {
        course_public_key: course.course_public_key,
      }

      if (runPublicKey) {
        body.run_public_key = runPublicKey
      }

      const response = await fetch(`${bffUrl}/enrollment/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || '決済セッションの作成に失敗しました')
      }

      const data = await response.json()

      // Stripe Checkoutへリダイレクト
      if (data.url) {
        window.location.href = data.url
      } else {
        throw new Error('Checkout URLが取得できませんでした')
      }
    } catch (err: any) {
      console.error('Error creating checkout session:', err)
      setError(err.message || '決済セッションの作成に失敗しました')
      setCreating(false)
    }
  }

  if (loading) {
    return (
      <MainLayout>
        <Loading message="講座情報を読み込み中..." />
      </MainLayout>
    )
  }

  if (!course || !price) {
    return (
      <MainLayout>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <ErrorMessage message={error || '講座情報が見つかりませんでした'} />
        </div>
      </MainLayout>
    )
  }

  // 既に受講権を持っている場合
  if (existingEntitlement) {
    return (
      <MainLayout>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-green-900 mb-2">
                  この講座は受講可能です
                </h3>
                <p className="text-sm text-green-800 mb-4">
                  既にこの講座の受講権をお持ちです。マイページから講座を開始できます。
                </p>
                <button
                  onClick={() => navigate('/mypage')}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition text-sm font-medium"
                >
                  マイページへ戻る
                </button>
              </div>
            </div>
          </div>
        </div>
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">申込内容の確認</h1>

        {error && (
          <div className="mb-6">
            <ErrorMessage message={error} />
          </div>
        )}

        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* 講座情報 */}
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">{course.title}</h2>
            <p className="text-gray-600 mb-4">{course.description || '講座の説明はありません'}</p>

            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <BookOpen className="h-4 w-4" />
                <span>認証団体: {course.tenant_id}</span>
              </div>

              {course.ambassador_name && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span>講師: {course.ambassador_name}</span>
                </div>
              )}

              {course.access_policy === 'relative_days' && course.access_days && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock className="h-4 w-4" />
                  <span>受講期間: {course.access_days}日間</span>
                </div>
              )}

              {course.access_policy === 'fixed_range' && course.access_start && course.access_end && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar className="h-4 w-4" />
                  <span>
                    受講期間: {new Date(course.access_start).toLocaleDateString('ja-JP')} 〜{' '}
                    {new Date(course.access_end).toLocaleDateString('ja-JP')}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* 価格情報 */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-gray-500" />
                <span className="text-lg font-medium text-gray-900">受講料</span>
              </div>
              <span className="text-2xl font-bold text-gray-900">¥{price.amount.toLocaleString()}</span>
            </div>
            {price.description && (
              <p className="mt-2 text-sm text-gray-600">{price.description}</p>
            )}
          </div>

          {/* アクション */}
          <div className="p-6 bg-gray-50">
            <button
              onClick={handleCheckout}
              disabled={creating}
              className="w-full py-3 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition"
            >
              {creating ? '決済ページへ移動中...' : '申込む'}
            </button>

            <button
              onClick={() => navigate('/mypage')}
              disabled={creating}
              className="w-full mt-3 py-2 px-4 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition text-sm disabled:opacity-50"
            >
              キャンセル
            </button>
          </div>
        </div>

        {/* 注意事項 */}
        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-800">
              <p className="font-medium mb-1">ご注意</p>
              <ul className="list-disc list-inside space-y-1">
                <li>決済完了後、すぐに受講を開始できます</li>
                <li>決済はStripeを通じて安全に処理されます</li>
                <li>領収書は登録メールアドレスに送信されます</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  )
}
